import { safeProduce as produce } from "~helpers/immer";

import * as at from "./actionTypes";

/**
 * @typedef {object} BillingSettings
 * @property {number} id
 * @property {BillItemDetails[]} items
 * @property {string} currency_code
 * @property {number} project
 */

/**
 * @typedef {object} InvoiceSettings
 * @property {number} id
 * @property {string} receiver_tax_code
 * @property {string} receiver_name
 * @property {string} receiver_address
 * @property {string} receiver_city
 * @property {string} receiver_town
 * @property {string} receiver_postal_code
 * @property {string[]} receiver_inbox_tags
 * @property {number} project
 */

/**
 * @typedef {object} InvoiceRecipient
 * @property {number} id
 * @property {string} receiver_tax_code
 * @property {string} receiver_name
 * @property {string} receiver_address
 * @property {string} receiver_city
 * @property {string} receiver_town
 * @property {string} receiver_postal_code
 * @property {number} project
 */

/**
 * @typedef {object} Invoice
 * @property {number} id
 * @property {number} bill
 * @property {number} invoice_settings
 * @property {string} invoice_number
 * @property {string} invoice_code
 * @property {string} invoice_ettn
 * @property {string} invoice_receiver_inbox_tag
 * @property {string} invoice_pdf
 * @property {string} invoice_status
 * @property {string} invoice_detail_status
 * @property {string} invoice_type
 * @property {number} project
 */

/**
 * @typedef {object} BillItemDetails
 * @property {number} id
 * @property {string} item_name
 * @property {string} item_unit
 * @property {number} item_unit_price
 * @property {number} project
 */

/**
 * @typedef {object} BillItem
 * @property {number} id
 * @property {number | null} billing_settings_item
 * @property {BillItemDetails | null} bill_item_details
 * @property {number} quantity
 * @property {number} vat_rate
 * @property {number} vat_amount
 * @property {number} base_amount
 * @property {string} tax_exemption_reason_code
 * @property {number} project
 */

/**
 * @typedef {object} Bill
 * @property {number} id
 * @property {BillItem[]} items
 * @property {number} billing_settings
 * @property {string} due_date
 * @property {string} billing_period_start
 * @property {string} billing_period_end
 * @property {number} total_base_amount
 * @property {number} total_vat_amount
 * @property {string} status
 * @property {string[]} notes
 * @property {Invoice[]} invoices
 * @property {number} project
 */

export const initState = {
  /** @type {Bill} */
  bill: null,
  /** @type {Invoice} */
  invoice: null,
  /** @type {InvoiceSettings} */
  invoiceSettings: null,
  /** @type {BillingSettings} */
  billSettingList: [],
  /** @type {BillItemDetails} */
  billSettingItem: null,
  /** @type {InvoiceRecipient} */
  invoiceRecipient: null,
  /** @type {InvoiceRecipient[]} */
  invoiceRecipientList: [],
  /** @type {Invoice[]} */
  invoiceList: [],
  /** @type {Bill[]} */
  billList: [],
  /** @type {InvoiceSettings[]} */
  invoiceSettingsList: [],
};

const billingAndInvoicing = (state = initState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case at.SET_BILL: {
        draft.bill = action.payload;
        break;
      }
      case at.SET_INVOICE: {
        draft.invoice = action.payload;
        break;
      }
      case at.SET_INVOICE_SETTINGS: {
        draft.invoiceSettings = action.payload;
        break;
      }
      case at.SET_BILL_SETTING_LIST: {
        draft.billSettingList = action.payload;
        break;
      }
      case at.SET_BILL_SETTING_ITEM: {
        draft.billSettingItem = action.payload;
        break;
      }
      case at.SET_INVOICE_RECIPIENT: {
        draft.invoiceRecipient = action.payload;
        break;
      }
      case at.SET_INVOICE_RECIPIENT_LIST: {
        draft.invoiceRecipientList = action.payload;
        break;
      }
      case at.SET_INVOICE_LIST: {
        draft.invoiceList = action.payload;
        break;
      }
      case at.SET_BILL_LIST: {
        draft.billList = action.payload;
        break;
      }
      case at.SET_INVOICE_SETTINGS_LIST: {
        draft.invoiceSettingsList = action.payload;
        break;
      }
    }
  });

export default billingAndInvoicing;
